import { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useDatasets } from "../context/DatasetContext";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Checkbox } from "./ui/checkbox";
import GlobalSearch from "./GlobalSearch";
import { ThemePicker } from "./ThemePicker";
import { LOGO_URL, FAVICON_URL } from "../utils/brand";
import { usePersistedState } from "../hooks/usePersistedState";
import { useVersionCheck } from "../hooks/useVersionCheck";
import { 
  LayoutDashboard, Table2, Siren, UploadCloud, MapPin, Settings,
  ChevronRight, Database, ChevronDown, ChevronUp,
  Pencil, Check, X, Trash2, FolderPlus, Folder, FolderOpen,
  GripVertical, LogOut, Shield, User, CheckCircle, Menu,
} from "lucide-react";

const navItems = [
  { path: "/", icon: LayoutDashboard, label: "Dashboard" },
  { path: "/data", icon: Table2, label: "Data Table" },
  { path: "/map", icon: MapPin, label: "Donor Map" },
  { path: "/anomalies", icon: Siren, label: "Anomalies" },
  { path: "/upload", icon: UploadCloud, label: "Upload" },
  { path: "/settings", icon: Settings, label: "Settings" },
];

function DatasetItem({ dataset, selectedDatasets, toggleDataset, editingId, setEditingId, editName, setEditName, handleSaveEdit, handleCancelEdit, handleStartEdit, folders, moveDatasetToFolder, deleteDataset, userRole, onApprovalToggle, geocodingStats }) {
  const [showMoveMenu, setShowMoveMenu] = useState(false);
  const isOwner = dataset.is_owner !== false || userRole === 'superadmin' || userRole === 'user'; // all non-viewers can manage
  const isApproved = dataset.approved !== false;
  const geoStat = geocodingStats?.[dataset.id];

  return (
    <div
      className={`group p-2 rounded-sm border transition-colors ${
        selectedDatasets.includes(dataset.id)
          ? 'bg-primary/5 border-primary/20'
          : 'bg-background border-transparent hover:border-border'
      }`}
      data-testid={`dataset-selector-${dataset.id}`}
    >
      {editingId === dataset.id && isOwner ? (
        <div className="flex items-center gap-1">
          <Input
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            className="h-7 text-xs"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSaveEdit();
              if (e.key === 'Escape') handleCancelEdit();
            }}
          />
          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={handleSaveEdit}>
            <Check className="w-3 h-3 text-emerald-600" />
          </Button>
          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={handleCancelEdit}>
            <X className="w-3 h-3" />
          </Button>
        </div>
      ) : (
        <div className="flex items-start gap-2">
          <Checkbox
            id={dataset.id}
            checked={selectedDatasets.includes(dataset.id)}
            onCheckedChange={() => toggleDataset(dataset.id)}
            className="mt-0.5"
          />
          <div className="flex-1 min-w-0">
            <label
              htmlFor={dataset.id}
              className="text-xs font-medium cursor-pointer block truncate"
              title={dataset.name || dataset.filename}
            >
              {dataset.name || dataset.filename}
              {!dataset.is_owner && dataset.is_owner !== undefined && <span className="ml-1 text-[10px] text-muted-foreground">(shared)</span>}
            </label>
            <div className="flex items-center gap-2 flex-wrap text-[10px] text-muted-foreground mt-0.5">
              <span>{dataset.record_count?.toLocaleString()} records</span>
              {dataset.date_range && <span>· {dataset.date_range}</span>}
              {dataset.upload_date && <span>· {new Date(dataset.upload_date).toLocaleDateString()} {new Date(dataset.upload_date).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'})}</span>}
              {geoStat && geoStat.total > 0 ? (
                <span
                  className={`inline-flex items-center gap-0.5 px-1 py-0 rounded text-[9px] font-semibold ${
                    geoStat.percentage >= 100
                      ? 'bg-emerald-100 text-emerald-700'
                      : geoStat.percentage > 0
                        ? 'bg-amber-100 text-amber-700'
                        : 'bg-muted text-muted-foreground'
                  }`}
                  title={`${geoStat.geocoded} of ${geoStat.total} addresses geocoded`}
                  data-testid={`geocoding-pct-${dataset.id}`}
                >
                  <MapPin className="w-2.5 h-2.5" />
                  {geoStat.percentage}%
                </span>
              ) : (dataset.processing_status !== 'processing' && (
                <span
                  className="inline-flex items-center gap-0.5 px-1 py-0 rounded text-[9px] font-semibold bg-muted text-muted-foreground/70"
                  title="Geocoding stats are still being computed — will appear shortly"
                  data-testid={`geocoding-pct-pending-${dataset.id}`}
                >
                  <MapPin className="w-2.5 h-2.5 animate-pulse" />
                  <span className="tabular-nums">…</span>
                </span>
              ))}
            </div>
          </div>
          <div className="relative flex items-center gap-0.5">
            {/* Approval toggle — superadmin only */}
            {userRole === 'superadmin' && (
              <button
                onClick={() => onApprovalToggle(dataset.id)}
                className={`h-6 w-6 flex items-center justify-center rounded transition-colors ${isApproved ? 'text-emerald-600 hover:text-emerald-700' : 'text-muted-foreground/40 hover:text-muted-foreground'}`}
                title={isApproved ? 'Approved — click to unapprove' : 'Not approved — click to approve'}
                data-testid={`approve-dataset-${dataset.id}`}
              >
                <CheckCircle className="w-3.5 h-3.5" />
              </button>
            )}
            {/* Edit/Move/Delete — owner only, not viewers */}
            {isOwner && userRole !== 'viewer' && (
              <div className="flex opacity-0 group-hover:opacity-100 transition-opacity">
                <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => handleStartEdit(dataset)}>
                  <Pencil className="w-3 h-3" />
                </Button>
                <Button
                  size="icon" variant="ghost" className="h-6 w-6"
                  onClick={() => setShowMoveMenu(!showMoveMenu)}
                  title="Move to folder"
                >
                  <GripVertical className="w-3 h-3" />
                </Button>
                <Button
                  size="icon" variant="ghost" className="h-6 w-6 text-red-500 hover:text-red-600"
                  onClick={() => { if (window.confirm(`Delete "${dataset.name || dataset.filename}" and all its ${dataset.record_count?.toLocaleString() || ''} records?`)) deleteDataset(dataset.id); }}
                  title="Delete dataset"
                  data-testid={`delete-dataset-${dataset.id}`}
                >
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            )}
            {showMoveMenu && (
              <div className="absolute right-0 top-full mt-1 z-50 bg-popover border rounded-sm shadow-lg py-1 w-40" onMouseLeave={() => setShowMoveMenu(false)}>
                {dataset.folder_id && (
                  <button
                    className="w-full text-left px-3 py-1.5 text-xs hover:bg-muted transition-colors"
                    onClick={() => { moveDatasetToFolder(dataset.id, null); setShowMoveMenu(false); }}
                  >
                    Remove from folder
                  </button>
                )}
                {folders.map(f => (
                  f.id !== dataset.folder_id && (
                    <button
                      key={f.id}
                      className="w-full text-left px-3 py-1.5 text-xs hover:bg-muted transition-colors flex items-center gap-2"
                      onClick={() => { moveDatasetToFolder(dataset.id, f.id); setShowMoveMenu(false); }}
                    >
                      <Folder className="w-3 h-3" /> {f.name}
                    </button>
                  )
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function FolderGroup({ folder, datasets, selectedDatasets, toggleDataset, toggleFolder, renameFolder, deleteFolder, editingId, setEditingId, editName, setEditName, handleSaveEdit, handleCancelEdit, handleStartEdit, folders, moveDatasetToFolder, deleteDataset, userRole, onApprovalToggle, geocodingStats }) {
  const [open, setOpen] = usePersistedState(`ui.sidebar.folder.${folder.id}.open`, false);
  const [editingFolder, setEditingFolder] = useState(false);
  const [folderName, setFolderName] = useState(folder.name);
  const isFolderOwner = folder.is_owner !== false || userRole === 'superadmin' || userRole === 'user';
  const folderDatasets = datasets.filter(d => d.folder_id === folder.id)
    .sort((a, b) => (a.name || a.filename).toLowerCase().localeCompare((b.name || b.filename).toLowerCase()));
  const allSelected = folderDatasets.length > 0 && folderDatasets.every(d => selectedDatasets.includes(d.id));
  const someSelected = folderDatasets.some(d => selectedDatasets.includes(d.id));

  // Folder-level geocoding status
  const folderGeoStats = folderDatasets.map(d => geocodingStats?.[d.id]).filter(Boolean);
  const hasGeoData = folderGeoStats.length > 0;
  const allFullyGeocoded = hasGeoData && folderGeoStats.length === folderDatasets.length && folderGeoStats.every(s => s.percentage >= 100);

  return (
    <div className="rounded-sm border border-border/50" data-testid={`folder-${folder.id}`}>
      <div className="flex items-center gap-1.5 px-2 py-1.5 bg-muted/40 group w-full">
        <Checkbox
          checked={allSelected}
          ref={el => { if (el) el.dataset.indeterminate = someSelected && !allSelected ? 'true' : 'false'; }}
          onCheckedChange={() => toggleFolder(folder.id)}
          className="h-3.5 w-3.5 shrink-0"
        />
        <button onClick={() => setOpen(!open)} className="flex items-center gap-1.5 flex-1 min-w-0">
          {open ? <FolderOpen className="w-3.5 h-3.5 text-amber-500 shrink-0" /> : <Folder className="w-3.5 h-3.5 text-amber-500 shrink-0" />}
          {editingFolder ? (
            <div className="flex items-center gap-1 flex-1" onClick={e => e.stopPropagation()}>
              <Input
                value={folderName}
                onChange={(e) => setFolderName(e.target.value)}
                className="h-6 text-xs flex-1"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { renameFolder(folder.id, folderName); setEditingFolder(false); }
                  if (e.key === 'Escape') { setFolderName(folder.name); setEditingFolder(false); }
                }}
              />
              <Button size="icon" variant="ghost" className="h-5 w-5" onClick={() => { renameFolder(folder.id, folderName); setEditingFolder(false); }}>
                <Check className="w-3 h-3 text-emerald-600" />
              </Button>
            </div>
          ) : (
            <span className="text-xs font-semibold truncate text-left">{folder.name}</span>
          )}
        </button>
        {isFolderOwner && userRole !== 'viewer' && (
          <div className="hidden group-hover:flex shrink-0">
            <Button size="icon" variant="ghost" className="h-5 w-5" onClick={(e) => { e.stopPropagation(); setEditingFolder(true); }}>
              <Pencil className="w-2.5 h-2.5" />
            </Button>
            <Button size="icon" variant="ghost" className="h-5 w-5 text-red-500 hover:text-red-600" onClick={(e) => { e.stopPropagation(); deleteFolder(folder.id); }}>
              <Trash2 className="w-2.5 h-2.5" />
            </Button>
          </div>
        )}
        <div className="flex items-center gap-1.5 shrink-0">
          <span className="text-[10px] text-muted-foreground">{folderDatasets.length}</span>
          {hasGeoData && (
            <div
              className={`w-2 h-2 rounded-full ${allFullyGeocoded ? 'bg-emerald-500' : 'bg-red-400'}`}
              title={allFullyGeocoded ? 'All datasets fully geocoded' : 'Some datasets not fully geocoded'}
              data-testid={`folder-geo-status-${folder.id}`}
            />
          )}
        </div>
      </div>
      {open && folderDatasets.length > 0 && (
        <div className="px-1 pb-1 space-y-0.5 pt-0.5">
          {folderDatasets.map(ds => (
            <DatasetItem
              key={ds.id} dataset={ds}
              selectedDatasets={selectedDatasets} toggleDataset={toggleDataset}
              editingId={editingId} setEditingId={setEditingId}
              editName={editName} setEditName={setEditName}
              handleSaveEdit={handleSaveEdit} handleCancelEdit={handleCancelEdit}
              handleStartEdit={handleStartEdit}
              folders={folders} moveDatasetToFolder={moveDatasetToFolder}
              deleteDataset={deleteDataset}
              userRole={userRole} onApprovalToggle={onApprovalToggle}
              geocodingStats={geocodingStats}
            />
          ))}
        </div>
      )}
      {open && folderDatasets.length === 0 && (
        <div className="px-3 pb-2 pt-1" data-testid={`folder-empty-${folder.id}`}>
          <p className="text-[10px] text-muted-foreground/70 italic">
            Empty folder — use &ldquo;Move to folder&rdquo; on any dataset below to add one here.
          </p>
        </div>
      )}
    </div>
  );
}

export default function DashboardLayout() {
  const {
    datasets, folders, selectedDatasets, fetchError, geocodingStats,
    toggleDataset, selectAll, selectNone, toggleFolder,
    updateDatasetName, deleteDataset, moveDatasetToFolder,
    createFolder, renameFolder, deleteFolder, fetchDatasets,
  } = useDatasets();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showDatasets, setShowDatasets] = usePersistedState("ui.sidebar.showDatasets", true);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");

  // Watch for a new deployment (bumped app_version) and prompt to refresh.
  useVersionCheck();

  // Close sidebar on route change (mobile)
  useEffect(() => { setSidebarOpen(false); }, [location.pathname]);

  const handleStartEdit = (dataset) => {
    setEditingId(dataset.id);
    setEditName(dataset.name || dataset.filename);
  };
  const handleSaveEdit = async () => {
    if (editingId && editName.trim()) await updateDatasetName(editingId, editName.trim());
    setEditingId(null); setEditName("");
  };
  const handleCancelEdit = () => { setEditingId(null); setEditName(""); };

  const handleCreateFolder = async () => {
    if (newFolderName.trim()) {
      await createFolder(newFolderName.trim());
      setNewFolderName("");
      setCreatingFolder(false);
    }
  };

  const handleApprovalToggle = async (datasetId) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/datasets/${datasetId}/approve`, {
        method: 'PATCH',
        headers: { ...(() => { const t = localStorage.getItem('fec_token'); return t ? { Authorization: `Bearer ${t}` } : {}; })() },
      });
      if (res.ok) fetchDatasets();
    } catch (e) { console.error('Failed to toggle approval:', e); }
  };

  // Split datasets into own and shared
  // All datasets visible — no own/shared split
  const ungroupedDatasets = [...datasets]
    .filter(d => !d.folder_id)
    .sort((a, b) => (a.name || a.filename).toLowerCase().localeCompare((b.name || b.filename).toLowerCase()));

  const sharedProps = {
    selectedDatasets, toggleDataset, editingId, setEditingId,
    editName, setEditName, handleSaveEdit, handleCancelEdit, handleStartEdit,
    folders, moveDatasetToFolder, deleteDataset,
    userRole: user?.role, onApprovalToggle: handleApprovalToggle,
    geocodingStats,
  };

  return (
    <div className="flex h-screen overflow-hidden bg-background" data-testid="dashboard-layout">
      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 md:hidden"
          onClick={() => setSidebarOpen(false)}
          data-testid="sidebar-backdrop"
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-40 w-72 border-r border-border bg-background flex flex-col transform transition-transform duration-200 ease-in-out md:relative md:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`} data-testid="sidebar">
        {/* Logo — dark navy backdrop blends with the logo's own baked-in
            background so the sidebar header reads as one intentional
            brand block, not a dark rectangle floating on a light sidebar. */}
        <div className="h-40 flex items-center justify-center px-4 bg-slate-900 border-b border-slate-800">
          <img
            src={LOGO_URL}
            alt="FEC Watchdog"
            className="max-h-28 w-auto max-w-full object-contain select-none drop-shadow-[0_0_14px_rgba(14,165,233,0.3)]"
            draggable={false}
            data-testid="sidebar-logo"
          />
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-1">
          {navItems.filter(item => {
            if (item.path === '/settings' && user?.role !== 'superadmin') return false;
            if (item.path === '/upload' && user?.role === 'viewer') return false;
            return true;
          }).map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === "/"}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium transition-all duration-75 group ${
                  isActive
                    ? "bg-primary text-primary-foreground highlight-bar"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                }`
              }
              data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
            >
              <item.icon className="w-4 h-4" />
              <span className="flex-1">{item.label}</span>
              <ChevronRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
            </NavLink>
          ))}
        </nav>

        {/* Dataset Selector */}
        <div className="flex-1 border-t border-border flex flex-col min-h-0">
          <button
            onClick={() => setShowDatasets(!showDatasets)}
            className="w-full px-4 py-3 flex items-center justify-between text-sm font-medium hover:bg-muted/50 transition-colors shrink-0"
            data-testid="toggle-datasets"
          >
            <div className="flex items-center gap-2">
              <Database className="w-4 h-4" />
              <span>Datasets</span>
              {selectedDatasets.length > 0 && (
                <span className="text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded">
                  {selectedDatasets.length} selected
                </span>
              )}
            </div>
            {showDatasets ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>

          {/* Flashing hint when no datasets selected */}
          {selectedDatasets.length === 0 && datasets.length > 0 && (
            <div className="mx-3 mb-2 animate-pulse" data-testid="dataset-hint">
              <div className="px-3 py-2 rounded-lg border-2 border-amber-400 bg-amber-50 text-center">
                <p className="text-xs font-semibold text-amber-700">Click a Dataset to get started</p>
              </div>
              <div className="flex justify-center mt-1">
                <ChevronDown className="w-5 h-5 text-amber-500 animate-bounce" />
              </div>
            </div>
          )}

          {showDatasets && (
            <div className="px-4 pb-4 space-y-2 animate-fade-in flex-1 min-h-0 flex flex-col">
              {/* Quick actions */}
              {datasets.length > 0 && (
                <div className="flex gap-2 mb-1 shrink-0">
                  <Button variant="outline" size="sm" className="text-xs flex-1 h-7" onClick={selectAll} data-testid="select-all-datasets">All</Button>
                  <Button variant="outline" size="sm" className="text-xs flex-1 h-7" onClick={selectNone} data-testid="select-none-datasets">None</Button>
                  {user?.role !== 'viewer' && (
                    <Button
                      variant="outline" size="sm" className="text-xs h-7 px-2"
                      onClick={() => setCreatingFolder(true)}
                      data-testid="create-folder-btn"
                      title="New folder"
                    >
                      <FolderPlus className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              )}

              {/* New folder input */}
              {creatingFolder && (
                <div className="flex items-center gap-1 shrink-0">
                  <Folder className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                  <Input
                    value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                    placeholder="Folder name..."
                    className="h-7 text-xs flex-1"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleCreateFolder();
                      if (e.key === 'Escape') { setCreatingFolder(false); setNewFolderName(""); }
                    }}
                  />
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={handleCreateFolder}>
                    <Check className="w-3 h-3 text-emerald-600" />
                  </Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => { setCreatingFolder(false); setNewFolderName(""); }}>
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              )}

              {datasets.length === 0 && fetchError ? (
                <div className="text-center py-4">
                  <p className="text-xs text-red-500 mb-2">Failed to load datasets</p>
                  <Button variant="outline" size="sm" className="text-xs h-7" onClick={() => fetchDatasets()} data-testid="retry-fetch-btn">Retry</Button>
                </div>
              ) : datasets.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-4">No datasets uploaded yet</p>
              ) : (
                <div className="space-y-2 flex-1 overflow-y-auto">
                  {/* All Folders — including empty ones, so a folder is
                      visible immediately after you create it (before any
                      dataset is assigned). Empty folders render with a
                      "0" count so users know they still need to fill them. */}
                  {folders.slice().sort((a, b) => a.name.localeCompare(b.name)).map(folder => (
                    <FolderGroup
                      key={folder.id}
                      folder={folder}
                      datasets={datasets}
                      toggleFolder={toggleFolder}
                      renameFolder={renameFolder}
                      deleteFolder={deleteFolder}
                      {...sharedProps}
                    />
                  ))}

                  {/* Ungrouped datasets */}
                  {ungroupedDatasets.length > 0 && folders.length > 0 && (
                    <div className="pt-1">
                      <p className="text-[10px] uppercase tracking-wider text-muted-foreground px-1 mb-1">Ungrouped</p>
                    </div>
                  )}
                  {ungroupedDatasets.map(ds => (
                    <DatasetItem key={ds.id} dataset={ds} {...sharedProps} />
                  ))}

                </div>
              )}
            </div>
          )}
        </div>

      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col h-screen overflow-hidden md:ml-0">
        <div className="h-14 border-b border-border bg-background flex items-center px-4 md:px-6 shrink-0" data-testid="top-header">
          <button
            onClick={() => setSidebarOpen(true)}
            className="md:hidden mr-3 p-1.5 rounded-md hover:bg-muted transition-colors"
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <GlobalSearch />
          </div>
          <div className="flex items-center gap-2 ml-4">
            <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
              {user?.role === 'superadmin' ? <Shield className="w-3.5 h-3.5 text-primary" /> : user?.role === 'viewer' ? <User className="w-3.5 h-3.5 text-muted-foreground" /> : <User className="w-3.5 h-3.5 text-primary" />}
            </div>
            <div className="min-w-0">
              <p className="text-xs font-medium truncate leading-tight" data-testid="user-display-name">{user?.name || user?.email}</p>
              <p className="text-[10px] text-muted-foreground truncate leading-tight">{user?.role === 'superadmin' ? 'Super Admin' : user?.role === 'viewer' ? 'Viewer' : 'User'}</p>
            </div>
            <ThemePicker />
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 shrink-0"
              onClick={() => { logout(); navigate('/login'); }}
              title="Sign out"
              data-testid="logout-btn"
            >
              <LogOut className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
        <div
          className="flex-1 overflow-auto relative"
          style={{
            backgroundImage: `url(${FAVICON_URL})`,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center center',
            backgroundSize: 'min(560px, 55vh) auto',
            backgroundAttachment: 'local',
          }}
        >
          {/* Soft veil so the watermark stays a whisper behind actual content
              without washing out cards or text. */}
          <div className="absolute inset-0 pointer-events-none bg-background/85" aria-hidden />
          <div className="relative">
            <Outlet />
          </div>
        </div>
        <footer className="h-8 border-t border-border bg-muted/30 flex items-center justify-center px-4 shrink-0" data-testid="app-footer">
          <FooterText />
        </footer>
      </main>
    </div>
  );
}

const DEFAULT_FOOTER = "\u00a9 2026 FEC Watchdog \u2014 Created by Bill Casso";
const FOOTER_CACHE_KEY = "app.footer.cache";
// Auto-appended to whatever the superadmin sets — so version bumps in
// `frontend/package.json` show up in the footer without a code edit.
// eslint-disable-next-line
const APP_VERSION = require("../../package.json").version;

function FooterText() {
  // Hydrate from the last-known footer text in localStorage so a hard
  // refresh doesn't flash the DEFAULT_FOOTER for a beat before the
  // /api/system/footer fetch lands. The cache is written on every
  // successful GET so it stays in step with the true value.
  const [text, setText] = useState(() => {
    try {
      const cached = window.localStorage.getItem(FOOTER_CACHE_KEY);
      if (cached) return cached;
    } catch (_) { /* private mode / quota */ }
    return DEFAULT_FOOTER;
  });
  useEffect(() => {
    const load = () => {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/system/footer`)
        .then((r) => r.ok ? r.json() : null)
        .then((d) => {
          if (d?.text) {
            setText(d.text);
            try { window.localStorage.setItem(FOOTER_CACHE_KEY, d.text); } catch (_) { /* ignore */ }
          }
        })
        .catch(() => {});
    };
    load();
    // Re-fetch when the superadmin saves a new footer so the running
    // tab updates immediately without needing a reload.
    const handler = () => load();
    window.addEventListener("footer-updated", handler);
    return () => window.removeEventListener("footer-updated", handler);
  }, []);
  return (
    <p className="text-[11px] text-muted-foreground">
      {text}
      <span className="opacity-60 ml-1">&middot; v{APP_VERSION}</span>
      <span className="opacity-40 mx-2">|</span>
      <a
        href="/privacy"
        className="opacity-70 hover:opacity-100 hover:underline underline-offset-2 transition-opacity"
        data-testid="footer-privacy-link"
      >
        Copyright &amp; Privacy
      </a>
    </p>
  );
}

